import { ArrowLeftOutlined } from "@ant-design/icons";
import { Link, useMatches } from "@tanstack/react-router";
import { Breadcrumb } from "antd";
import { ItemType } from "antd/es/breadcrumb/Breadcrumb";
import { tenantLocationAtom } from "atoms/tenantLocationAtom.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { useRecoilValue } from "recoil";
import { RequiredStaticData } from "utils/createTenantFileRoute";
import { isDynamicParam } from "utils/isUUID.ts";

interface BreadcrumbsProps {
    className?: string;
}

export const Breadcrumbs: FC<BreadcrumbsProps> = (props) => {
    const { t } = usePaldockTranslation("common");
    const matches = useMatches();
    const tenantLocationState = useRecoilValue(tenantLocationAtom);

    const hasDynamicParam = tenantLocationState.paths.some(isDynamicParam);

    if (matches.some(({ status }) => status === "pending")) {
        return null;
    }

    // Check if any route has showBreadcrumbsBar set to false
    const shouldHideBreadcrumbs = matches.some((match) => {
        const staticData = match.staticData as RequiredStaticData & { showBreadcrumbsBar?: boolean };
        return staticData && staticData.showBreadcrumbsBar === false;
    });

    if (shouldHideBreadcrumbs) {
        return null;
    }

    const matchesWithBreadcrumbs = matches.filter(
        (match): match is typeof match & { loaderData: { breadcrumb: string; asActive?: boolean } } =>
            !!match.loaderData &&
            typeof match.loaderData === "object" &&
            "breadcrumb" in match.loaderData &&
            typeof match.loaderData.breadcrumb === "string"
    );

    const itemsRaw: Array<ItemType & { asActive?: boolean }> = matchesWithBreadcrumbs.map(
        ({ pathname, loaderData: { breadcrumb, asActive } }, index) => {
            const isLastItem = index === matchesWithBreadcrumbs.length - 1;
            const isNextToLastItem = index === matchesWithBreadcrumbs.length - 2;
            const pathHasDynamicParam = pathname.split("/").some(isDynamicParam);

            let href: string | undefined;

            if (isLastItem) {
                // Last item is never clickable
                href = undefined;
            } else if (hasDynamicParam && isNextToLastItem) {
                // If we have dynamic param and this is next-to-last, make it clickable with the same link as back button
                href = pathname;
            } else if (pathHasDynamicParam) {
                // If the current path has dynamic param, don't make it clickable
                href = undefined;
            } else {
                // Normal breadcrumb, make it clickable
                href = pathname;
            }

            const shouldShowAsClickable = hasDynamicParam && isNextToLastItem && href;

            return {
                href,
                title: breadcrumb,
                className: `${!href || (asActive && !shouldShowAsClickable) ? "!text-black" : undefined}`,
                asActive: Boolean(asActive),
            };
        }
    );

    const itemsToRender: ItemType[] = itemsRaw.map(({ href, asActive, ...rest }, index) => {
        const isNextToLastItem = index === itemsRaw.length - 2;
        const shouldKeepHref = hasDynamicParam && isNextToLastItem && href;

        return {
            href: asActive && !shouldKeepHref ? undefined : href,
            ...rest,
        };
    });

    const getBreadcrumbLinkTo = () => {
        let i = itemsRaw.length - 1;

        while (i > 0 && (!itemsRaw[i].href || itemsRaw[i].href!.split("/").some(isDynamicParam))) {
            itemsRaw.pop();
            i--;
        }

        return itemsRaw[i].href || "/";
    };

    return (
        <div className={`flex items-center gap-2 ${props.className ?? ""}`}>
            {hasDynamicParam && itemsToRender.length > 1 && (
                <Link
                    to={getBreadcrumbLinkTo()}
                    replace={false}
                    search={{}}
                    className="flex items-center gap-1 text-black p-1 rounded cursor-pointer transition-colors duration-150 hover:bg-[rgba(0,0,0,0.06)]"
                >
                    <ArrowLeftOutlined />
                    <span>{t("back")}</span>
                </Link>
            )}
            <Breadcrumb items={itemsToRender} />
        </div>
    );
};
